import BiobankData from 'BrdData/BiobankData';
import { OBJECT_STATUS, user_roles } from 'BrdData/Enum';
import { NotesListPanel } from 'Components';
import {
  CancelButton,
  DeleteButton,
  StatusToggleButton,
} from 'Components/Buttons';
import { SITE_PAGES } from 'Config';
import {
  useAlertContext,
  useAuthContext,
  useDrawerContext,
  useToast,
} from 'Context';
import { DataManager } from 'Lib/data';
import { useStateWithRef } from 'Lib/hooks';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { isUserAuthorized } from 'Util';
import BiobankInformationPanel from './BiobankInformationPanel';
import styles from './Biobanks.module.css';

let DM = new DataManager();

const BiobankDetailsView = ({ biobankId, params, isNew = false, ...props }) => {
  const navigate = useNavigate();
  const { appUserId } = useAuthContext();
  const { openDrawer, closeDrawer } = useDrawerContext();
  const { showSuccessToast } = useToast();

  const [biobank, setBiobank, biobankRef] = useStateWithRef({});
  const idRef = useRef();
  const [editable, setEditable] = useState(
    isUserAuthorized({
      roles: [user_roles.ADMINISTRATOR, user_roles.SUPER_EDITOR],
    }),
  );

  const { openDeleteDialog, openConfirmationDialog, showDeletedToast } =
    useAlertContext();

  useEffect(() => {
    DM.add(
      BiobankData.registerListener('BLANK_RECORD_LOADED', onBiobankLoaded),
      BiobankData,
    );
    DM.add(
      BiobankData.registerListener('RECORD_LOADED', onBiobankLoaded),
      BiobankData,
    );
    DM.add(
      BiobankData.registerListener('RECORD_ACTIVATED', onBiobankActivated),
      BiobankData,
    );
    DM.add(
      BiobankData.registerListener('RECORD_DEACTIVATED', onBiobankDeactivated),
      BiobankData,
    );
    DM.add(
      BiobankData.registerListener('RECORDS_DELETED', onBiobankDeleted),
      BiobankData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    if (!biobankId && !params?.id) {
      BiobankData.getBlank();
    }
  }, []);

  useEffect(() => {
    if (!editable) {
      if (
        biobank?.user_id === appUserId &&
        isUserAuthorized({
          roles: [user_roles.EDITOR],
        })
      ) {
        setEditable(true);
      }
    }
  }, [biobank]);

  useEffect(() => {
    if (biobankId && biobankId > 0) {
      if (!idRef?.current || idRef.current !== biobankId) {
        getBiobank(biobankId);
      }
      idRef.current = biobankId;
    }
  }, [biobankId]);

  useEffect(() => {
    if (params?.id && !biobankId) {
      if (!idRef?.current || idRef.current !== params.id) {
        getBiobank(params.id);
      }
      idRef.current = params.id;
    }
  }, [params]);

  const getBiobank = (id) => {
    BiobankData.getBiobank(id);
  };

  const onBiobankLoaded = (payload) => {
    if (payload) {
      setBiobank(payload);
    }
  };

  let statusCancelButton =
    (editable || isNew) &&
    (isUserAuthorized({
      roles: [user_roles.SUPER_EDITOR],
    }) ||
      (isUserAuthorized({
        roles: [user_roles.EDITOR],
      }) &&
        (isNew || biobank?.user_id === appUserId))) &&
    (biobank?.id ? (
      <StatusToggleButton
        currentStatus={biobank.status}
        onStatusToggleClicked={() => {
          statusToggleClicked();
        }}
      />
    ) : (
      <CancelButton onCancel={closeDrawer} />
    ));

  const toggleBiobankStatus = () => {
    biobankRef.current?.status === OBJECT_STATUS.ACTIVE.key
      ? BiobankData.deactivateRecord(biobankRef.current?.id)
      : BiobankData.activateRecord(biobankRef.current?.id);
  };

  let currentStatusActive =
    biobankRef.current?.status === OBJECT_STATUS.ACTIVE.key;

  const statusToggleClicked = () => {
    openConfirmationDialog({
      onConfirm: toggleBiobankStatus,
      dialogTitle: currentStatusActive
        ? 'Deactivate Bio Resource Entry?'
        : 'Reactivate Bio Resource Entry?',
      icon: currentStatusActive ? 'disable' : undefined,
      intent: currentStatusActive ? 'inactive' : 'active',
    });
  };

  const onBiobankActivated = (payload) => {
    showSuccessToast('Bio Resource Entry Reactivated');
    getBiobank(payload.id);
  };

  const onBiobankDeactivated = (payload) => {
    showSuccessToast('Bio Resource Entry Deactivated');
    getBiobank(payload.id);
  };

  const onBiobankNotesUpdated = () => {
    getBiobank(idRef.current);
  };

  const deleteClicked = () => {
    openDeleteDialog({
      objectType: 'Bio Resource Entry',
      confirmText: 'Yes, delete!',
      onConfirmDelete: deleteBiobank,
    });
  };

  let delButton = editable && biobank?.id && !currentStatusActive && (
    <DeleteButton
      className={styles.delete_button}
      onDeleteItem={deleteClicked}
      tooltipText={'Delete Bio Resource Entry'}
      buttonText={'Delete'}
    />
  );

  const deleteBiobank = () => {
    BiobankData.deleteBiobank(idRef.current);
  };

  const onBiobankDeleted = () => {
    showDeletedToast('Bio Resource Entry');
    navigate(SITE_PAGES.BIOBANK_LANDING.path);
  };

  return (
    biobank && (
      <div
        style={{
          display: 'flex',
          gap: 10,
          flexDirection: 'column',
          maxWidth: 800,
        }}>
        <BiobankInformationPanel
          title='Details'
          editable={editable}
          isNew={isNew}
          collapsible={false}
          {...props}
        />
        {!isNew && (
          <NotesListPanel
            notes={biobank?.biobank_notes || []}
            notesTableName='biobank_notes'
            dataIdentifier='BB_NOTES'
            canAddNotes={!!biobank.id}
            editable={editable}
            hideToggle
            parentEditState={editable || !!biobank.id}
            newNoteFieldValues={{ biobank_id: biobank.id }}
            onNotesUpdated={onBiobankNotesUpdated}
            collapsible
          />
        )}
        <div className={styles.biobank_button_container}>
          {statusCancelButton}
          {delButton}
        </div>
      </div>
    )
  );
};

BiobankDetailsView.propTypes = {
  biobankId: PropTypes.number,
  params: PropTypes.shape({ id: PropTypes.string }),
  isNew: PropTypes.bool,
  compact: PropTypes.bool,
};

export default BiobankDetailsView;
